<template>
  <van-uploader
    v-bind="safeAttrs"
    v-model="fileList"
    :after-read="afterRead"
    :max-count="maxCount"
    @click-preview="onClickReupload"
    multiple
  >
    <template v-for="(_, name) in $slots" v-slot:[name]="slotData">
      <slot v-if="_" :name="name" v-bind="slotData" />
    </template>
  </van-uploader>
</template>

<script lang="ts" setup>
import { UploaderFileListItem } from "vant";
import { useI18n } from "vue-i18n";
import { ref } from "vue";
import axiosInstance from "@/api/axiosInstance";
import { SizeLimitedMap } from "@/utils/size-limited-map";
import { watch } from "vue";
import { useAttrs } from "vue";
import { computed } from "vue";
import { useSetting } from "@/composables/use-setting";
import { getUploadDomain } from "@/utils/env";

const props = withDefaults(
  defineProps<{
    uploadingMessage?: string;
    multiple?: boolean;
    maxCount?: number;
    modelValue: undefined | { url: string }[];
  }>(),
  {
    uploadingMessage: "",
    multiple: true,
    maxCount: 9,
  },
);

const { t } = useI18n();
const attrs = useAttrs();
const safeAttrs = computed(() => {
  return {
    ...attrs,
    modelValue: undefined,
  };
});

const emits = defineEmits<{
  (e: "update:modelValue", value: { url: string }[] | undefined): void;
}>();

const cacheFiles = new SizeLimitedMap();
cacheFiles.maxSize = 100;
const fileList = ref<UploaderFileListItem[]>([]);

const { getClientInfo } = useSetting();
const clientInfo = getClientInfo();

const userToken = clientInfo.userToken;

watch(
  () => {
    return props.modelValue;
  },
  (val) => {
    let urls: any[] = [];
    if (typeof val === "string") {
      urls = val ? [val] : [];
    } else if (Array.isArray(val)) {
      urls = val;
    }
    const files: any[] = [];
    for (const url of urls) {
      let file = cacheFiles.get(typeof url === "string" ? url : url.url);
      if (!file) {
        if (typeof url === "string") {
          file = {
            url,
          };
        } else if (typeof url === "object") {
          file = url;
        }
        cacheFiles.set(url, file);
      }
      files.push(file);
    }
    fileList.value = files;
  },
  {
    immediate: true,
  },
);

watch(
  fileList,
  (val) => {
    let finalVal: { url: string }[] | undefined;
    if (!props.multiple) {
      const firstItem = val[0];
      if (firstItem) {
        finalVal = firstItem as any;
      } else {
        finalVal = undefined;
      }
    } else {
      const urls: any[] = [];
      for (const item of val) {
        if (item.url) {
          cacheFiles.set(item.url, item);
          urls.push(item);
        } else if (item.objectUrl) {
          cacheFiles.set(item.objectUrl, item);
          urls.push(item);
        }
      }
      finalVal = urls;
    }
    if (JSON.stringify(finalVal) != JSON.stringify(props.modelValue)) {
      emits("update:modelValue", finalVal);
    }
  },
  {
    deep: true,
  },
);

const uploadFileRequest = async (file: UploaderFileListItem) => {
  if (!file.file) {
    return "";
  }
  const formData = new FormData();
  formData.append("file", file.file);
  formData.append("accessType", "vog-customer");
  formData.append("accessToken", userToken);
  return axiosInstance.post(getUploadDomain(), formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      clientinfo: userToken,
    },
  });
};

const uploadFile = (file: UploaderFileListItem) => {
  file.status = "uploading";
  file.message = props.uploadingMessage || t("message.uploading");
  return uploadFileRequest(file)
    .then((res: any) => {
      if (res.rtn_code === "0000" && res.data) {
        cacheFiles.delete(file.objectUrl);
        file.status = "done";
        file.url = res.data;
      } else {
        file.status = "failed";
        file.message = res.rtn_msg || t("message.uploadError");
      }
    })
    .catch(() => {
      file.status = "failed";
      file.message = t("message.uploadError");
    });
};

const afterRead = (file: UploaderFileListItem | UploaderFileListItem[]) => {
  const files = Array.isArray(file) ? file : [file];
  for (const file of files) {
    uploadFile(file);
  }
};
const onClickReupload = (file: UploaderFileListItem) => {
  if (file.status === "failed") {
    uploadFile(file);
  }
};

/**
 * 检查上传状态
 */
const checkUploadStatus = async () => {
  for (const file of fileList.value) {
    if (!file.url) {
      return new Error(
        t(
          file.status === "failed"
            ? "message.uploadFailed"
            : "message.uploading",
        ),
      );
    }
  }
  return false;
};

defineExpose({ checkUploadStatus });
</script>

<style lang="scss" scoped></style>
